/**
 * THE ONE PLACE that converts between protocol payloads and wire bytes.
 *
 * Off-by-one errors here are the single most likely bug class in this app, so the
 * report-ID framing contract lives in exactly one function pair and nowhere else.
 * See the block comment in constants.ts for the measured rules; the short version:
 *
 *   encodeFrame()  -> 63 bytes, NO report ID   (for sendReport(id, ...))
 *   decodeFrame()  -> reads the 63-byte payload the 'inputreport' event gives us
 *
 * Neither function ever sees a report ID. That is the point.
 */

import {
  ConfigOffset,
  JigState,
  MSG_CFG_HEADER_SIZE,
  MSG_PAYLOAD_SIZE,
  PatternKind,
  CFG_MS_PER_SEC,
} from './constants.ts';
import type { DeviceConfig, DeviceInfo, DeviceStatus, Frame, JigStep } from './types.ts';

/* --------------------------------------------------------------- scalars -- */

export function putU16(view: DataView, offset: number, value: number): void {
  view.setUint16(offset, value & 0xffff, true);
}

export function putU32(view: DataView, offset: number, value: number): void {
  view.setUint32(offset, value >>> 0, true);
}

export function getU16(view: DataView, offset: number): number {
  return view.getUint16(offset, true);
}

export function getU32(view: DataView, offset: number): number {
  return view.getUint32(offset, true);
}

/* ----------------------------------------------------------------- frame -- */

/** Build the 63-byte payload for sendReport(id, ...). NO report ID. */
export function encodeFrame(
  msgId: number,
  payload: Uint8Array = new Uint8Array(0),
  seq = 0,
  totalLenOverride?: number,
): Uint8Array {
  if (payload.length > MSG_PAYLOAD_SIZE) {
    throw new RangeError(`payload ${payload.length} exceeds ${MSG_PAYLOAD_SIZE} bytes — every command must fit in one frame`);
  }

  const frame = new Uint8Array(MSG_PAYLOAD_SIZE + 4);
  const view = new DataView(frame.buffer);
  const total = totalLenOverride ?? payload.length;

  frame[0] = msgId & 0xff;
  frame[1] = seq & 0xff;
  putU16(view, 2, total);
  frame.set(payload, 4);

  return frame;
}

/** Parse a 63-byte payload from the 'inputreport' event. NO report ID. */
export function decodeFrame(data: DataView): Frame {
  if (data.byteLength < 4) {
    throw new RangeError(`frame is ${data.byteLength} bytes, need at least 4`);
  }
  const totalLen = getU16(data, 2);

  /* Tolerant of a short buffer. In practice the device always sends a full 63-byte
     report (the declared report size), but assuming that here would turn any
     surprise -- a different firmware, a truncated transfer -- into a RangeError
     inside the inputreport handler. Clamp instead, and let totalLen drive how much
     of it is meaningful. */
  const available = data.byteLength - 4;
  const payload = new Uint8Array(data.buffer, data.byteOffset + 4, available);
  return {
    msgId: data.getUint8(0),
    seq: data.getUint8(1),
    totalLen,
    data: payload.slice(0, Math.min(totalLen, available)),
  };
}

/* ---------------------------------------------------------------- config -- */

/** Serialise a DeviceConfig into the 20-byte wire header. */
export function encodeConfig(cfg: DeviceConfig): Uint8Array {
  const out = new Uint8Array(MSG_CFG_HEADER_SIZE);
  const view = new DataView(out.buffer);

  out[ConfigOffset.KIND] = cfg.kind;
  out[ConfigOffset.FLAGS] = cfg.flags;
  out[ConfigOffset.JITTER] = cfg.jitter;
  out[ConfigOffset.SEED] = cfg.seed;

  // Milliseconds internally, seconds on the wire, rounded UP so a duration is
  // never reported as shorter than it is. The device rejects > 65535 s rather
  // than truncating, so the ceiling is enforced here too -- fail with a message
  // the user can act on instead of a NACK they cannot.
  const durationS = Math.ceil(cfg.durationMs / CFG_MS_PER_SEC);
  if (durationS > 0xffff) {
    throw new RangeError(`duration ${durationS} s exceeds the device limit of 65535 s (~18 h)`);
  }
  putU32(view, ConfigOffset.DURATION_S, durationS);

  putU16(view, ConfigOffset.AMPLITUDE, cfg.amplitude);
  putU16(view, ConfigOffset.PERIOD_MS, cfg.periodMs);
  putU16(view, ConfigOffset.STEP_COUNT, cfg.stepCount);
  putU16(view, ConfigOffset.BOOT_DELAY_MS, cfg.bootDelayMs);
  out[ConfigOffset.SAVED] = cfg.saved ? 1 : 0;

  return out;
}

/** Decode the 20-byte wire header. */
export function decodeConfig(payload: Uint8Array): DeviceConfig {
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  return {
    kind: payload[ConfigOffset.KIND] as DeviceConfig['kind'],
    flags: payload[ConfigOffset.FLAGS] ?? 0,
    jitter: payload[ConfigOffset.JITTER] ?? 0,
    seed: payload[ConfigOffset.SEED] ?? 0,
    durationMs: getU32(view, ConfigOffset.DURATION_S) * CFG_MS_PER_SEC,
    amplitude: getU16(view, ConfigOffset.AMPLITUDE),
    periodMs: getU16(view, ConfigOffset.PERIOD_MS),
    stepCount: getU16(view, ConfigOffset.STEP_COUNT),
    bootDelayMs: getU16(view, ConfigOffset.BOOT_DELAY_MS),
    saved: payload[ConfigOffset.SAVED] === 1,
  };
}

/* ------------------------------------------------------------- responses -- */

export function decodeInfo(payload: Uint8Array): DeviceInfo {
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  return {
    fwMajor: payload[0] ?? 0,
    fwMinor: payload[1] ?? 0,
    fwPatch: payload[2] ?? 0,
    protoVersion: payload[3] ?? 0,
    caps: getU16(view, 4),
    state: (payload[6] ?? 0) as DeviceInfo['state'],
    saved: payload[7] === 1,
  };
}

export function decodeStatus(payload: Uint8Array): DeviceStatus {
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  return {
    state: (payload[0] ?? 0) as DeviceStatus['state'],
    elapsedMs: getU32(view, 1),
    totalMs: getU32(view, 5),
    stepIndex: getU16(view, 9),
    stepsDone: getU32(view, 11),
  };
}

export function decodeSaveResult(payload: Uint8Array): { ok: boolean; halStatus: number } {
  return { ok: payload[0] === 1, halStatus: payload[1] ?? 0 };
}

/* ------------------------------------------------------------------ steps -- */

/** dx/dy/wheel are signed int8; `<< 24 >> 24` sign-extends correctly. */
export function decodeStep(view: DataView, offset: number): JigStep {
  return {
    dx: (view.getUint8(offset + 0) << 24) >> 24,
    dy: (view.getUint8(offset + 1) << 24) >> 24,
    wheel: (view.getUint8(offset + 2) << 24) >> 24,
    buttons: view.getUint8(offset + 3),
    delayMs: getU16(view, offset + 4),
  };
}

export function encodeStep(step: JigStep, out: Uint8Array, offset: number): void {
  const view = new DataView(out.buffer, out.byteOffset, out.byteLength);
  view.setUint8(offset + 0, step.dx & 0xff);
  view.setUint8(offset + 1, step.dy & 0xff);
  view.setUint8(offset + 2, step.wheel & 0xff);
  view.setUint8(offset + 3, step.buttons & 0xff);
  putU16(view, offset + 4, step.delayMs);
}

/** Build a PATTERN_DATA / PATTERN_CHUNK payload: index, count, then the steps. */
export function encodeSteps(index: number, steps: readonly JigStep[]): Uint8Array {
  const out = new Uint8Array(3 + steps.length * 6);
  const view = new DataView(out.buffer);
  putU16(view, 0, index);
  out[2] = steps.length;
  steps.forEach((s, i) => encodeStep(s, out, 3 + i * 6));
  return out;
}

export function decodeSteps(payload: Uint8Array): { index: number; steps: JigStep[] } {
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  const index = getU16(view, 0);
  const count = payload[2] ?? 0;
  const steps: JigStep[] = [];
  for (let i = 0; i < count; i++) {
    steps.push(decodeStep(view, 3 + i * 6));
  }
  return { index, steps };
}

/* --------------------------------------------------------------- helpers -- */

export function isKnownPatternKind(value: number): boolean {
  return Object.values(PatternKind).includes(value as never);
}

export function isKnownState(value: number): boolean {
  return Object.values(JigState).includes(value as never);
}

/** Total cursor path length of a step list, in px. Used by the disruption meter. */
export function stepPathLength(steps: readonly JigStep[]): number {
  return steps.reduce((sum, s) => sum + Math.abs(s.dx) + Math.abs(s.dy), 0);
}

/** One full cycle duration of a step list, in ms, honouring the device's floor. */
export function stepCycleMs(steps: readonly JigStep[]): number {
  return steps.reduce((sum, s) => sum + Math.max(s.delayMs, 2), 0);
}
